import React, { useState, useRef, useEffect } from "react";
import { Button, Typography, Modal, Box } from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import {
  getStorage,
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import {
  doc,
  getDoc,
  setDoc,
  arrayUnion,
  arrayRemove,
} from "firebase/firestore";

// ✅ Use your singletons so we use the same app/auth/db everywhere
import { auth, db } from "../firebase";

const VoiceRecorder = ({
  recorderLabel = "Read-Aloud Recorder",
  storyTitle = "Untitled Story",
}) => {
  const [recording, setRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [isSaved, setIsSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [existingRecordings, setExistingRecordings] = useState([]);

  const mediaRecorderRef = useRef(null);
  const chunks = useRef([]);
  const micRef = useRef(null);

  const maxRecordings = 3;

  const uid = auth.currentUser?.uid || null;
  const userDocRef = uid ? doc(db, "users", uid) : null;

  // Load current recordings (from users/{uid})
  const fetchRecordings = async () => {
    if (!userDocRef) return [];
    const snap = await getDoc(userDocRef);
    if (!snap.exists()) {
      setExistingRecordings([]);
      return [];
    }
    const data = snap.data() || {};
    const recs = Array.isArray(data.recordings) ? data.recordings : [];
    setExistingRecordings(recs);
    return recs;
  };

  const startRecording = async () => {
    setIsSaved(false);
    // Enforce limit before opening mic
    const recs = await fetchRecordings();
    if (recs.length >= maxRecordings) {
      setShowLimitModal(true);
      return;
    }

    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    micRef.current = stream;

    const mr = new MediaRecorder(stream);
    mediaRecorderRef.current = mr;

    mr.ondataavailable = (e) => {
      chunks.current.push(e.data);
    };

    mr.onstop = () => {
      const blob = new Blob(chunks.current, { type: "audio/webm" });
      chunks.current = [];
      if (blob.size > 0) {
        setAudioBlob(blob);
      } else {
        alert("Recording was too short or failed. Please try again.");
      }
    };

    mr.start();
    setRecording(true);
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop();
      setRecording(false);
    }
  };

  const resetRecording = () => {
    setAudioBlob(null);
    setRecording(false);
    setIsSaved(false);
    chunks.current = [];
  };

  const handleSaveRecording = async () => {
    if (!audioBlob) return;
    if (!uid) {
      alert("You must be signed in to save recordings.");
      return;
    }

    try {
      setSaving(true);

      // Upload to Storage (use default bucket from Firebase config)
      const fileName = `recording-${Date.now()}.webm`;
      const storage = getStorage(); // uses app’s configured bucket
      const storagePath = `recordings/${uid}/${fileName}`;
      const storageRef = ref(storage, storagePath);

      await uploadBytes(storageRef, audioBlob);
      const downloadURL = await getDownloadURL(storageRef);

      // Recording object (include id + storagePath for reliable deletes)
      const rec = {
        id: crypto.randomUUID(),
        fileName,
        storagePath,
        url: downloadURL,
        storyTitle,
        uploadedAt: Date.now(),
        sentToTeacher: false,
      };

      // ✅ Create doc if missing and append to recordings
      await setDoc(
        userDocRef,
        { recordings: arrayUnion(rec) },
        { merge: true }
      );

      // Refresh list & reset UI
      await fetchRecordings();
      setIsSaved(true);
      setAudioBlob(null);
    } catch (error) {
      console.error("❌ Error saving recording:", error);
      alert("Something went wrong while saving your recording.");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteRecording = async (rec) => {
    if (!uid) return;
    try {
      // Delete file from Storage
      const storage = getStorage();
      const path = rec.storagePath || `recordings/${uid}/${rec.fileName}`;
      await deleteObject(ref(storage, path));

      // Remove object from array (must match exactly)
      await setDoc(
        userDocRef,
        { recordings: arrayRemove(rec) },
        { merge: true }
      );

      const updated = await fetchRecordings();
      setExistingRecordings(updated);
    } catch (e) {
      console.error("Delete recording error:", e);
      alert("Failed to delete recording.");
    }
  };

  // Cleanup mic on unmount
  useEffect(() => {
    return () => {
      if (micRef.current) {
        micRef.current.getTracks().forEach((t) => t.stop());
      }
    };
  }, []);

  return (
    <div
      style={{
        marginTop: "30px",
        padding: "20px",
        backgroundColor: "rgba(255,255,255,0.95)",
        borderRadius: "10px",
      }}
    >
      <Typography
        variant="h6"
        style={{ fontFamily: "Montserrat, sans-serif", fontWeight: "bold", marginBottom: "15px" }}
      >
        {recording && (
          <div style={{ marginTop: "10px", color: "red", fontWeight: "bold" }}>
            🔴 Recording in progress...
          </div>
        )}
        🎤 {recorderLabel}
      </Typography>

      {audioBlob && (
        <div style={{ marginBottom: "10px" }}>
          <Typography variant="body1" style={{ fontWeight: "bold" }}>
            ▶️ Preview:
          </Typography>
          <audio controls src={URL.createObjectURL(audioBlob)} />
          {!isSaved && (
            <Typography style={{ marginTop: "10px", color: "#333", fontWeight: "bold" }}>
              ✅ You can now save this recording using the 💾 Save button below.
            </Typography>
          )}
        </div>
      )}

      <div>
        <Button
          variant="contained"
          color="secondary"
          onClick={startRecording}
          disabled={recording}
          style={{ marginRight: "10px", marginTop: "10px" }}
        >
          ▶️ Start
        </Button>

        <Button
          variant="contained"
          color="secondary"
          onClick={stopRecording}
          disabled={!recording}
          style={{ marginRight: "10px", marginTop: "10px" }}
        >
          ⏹️ Stop
        </Button>

        <Button
          variant="outlined"
          color="primary"
          onClick={resetRecording}
          style={{ marginRight: "10px", marginTop: "10px" }}
        >
          🔄 Reset
        </Button>

        <Button
          variant="contained"
          color="primary"
          onClick={handleSaveRecording}
          disabled={!audioBlob || recording || saving}
          style={{ marginTop: "10px", display: "flex", alignItems: "center", gap: "8px" }}
        >
          {saving ? (
            <>
              <CircularProgress size={20} color="inherit" />
              Saving...
            </>
          ) : (
            <>💾 Save</>
          )}
        </Button>
      </div>

      {isSaved && (
        <Typography style={{ color: "green", marginTop: "10px", fontWeight: "bold" }}>
          🎉 Your recording has been saved!
        </Typography>
      )}

      {/* Modal for limit reached */}
      <Modal open={showLimitModal} onClose={() => setShowLimitModal(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 400,
            bgcolor: "background.paper",
            borderRadius: 2,
            boxShadow: 24,
            p: 3,
          }}
        >
          <Typography variant="h6" gutterBottom>
            ❌ Recording Limit Reached
          </Typography>
          <Typography variant="body2" gutterBottom>
            You’ve reached your limit of 3 recordings. Please delete one to record a new one.
          </Typography>

          {existingRecordings.map((rec, index) => (
            <Box key={rec.id || index} sx={{ mt: 2, borderTop: "1px solid #ccc", pt: 1 }}>
              <Typography fontWeight="bold">{rec.storyTitle || rec.fileName}</Typography>
              <audio controls src={rec.url} style={{ width: "100%", marginTop: 5 }} />
              <Button
                variant="outlined"
                color="error"
                onClick={() => handleDeleteRecording(rec)}
                sx={{ mt: 1 }}
              >
                🗑️ Delete
              </Button>
            </Box>
          ))}
        </Box>
      </Modal>
    </div>
  );
};

export default VoiceRecorder;
